import { DomainList } from "@/components/domain";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetReservation } from "@/hooks/reservations";
import { T } from "@/i18n";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { BookOpen, PiggyBank } from "lucide-react";
import { PaymentCard } from "./-components/list-item";
import { useGetBalances } from "./-hooks/useGetBalance";
import { useGetDeposits } from "./-hooks/useGetDeposits";

export const Route = createFileRoute("/(payments)/payments/$id")({
  component: RouteComponent,
});

const tableDefinition = {
  hasActions: true,
  columns: [
    {
      header: "payments.fields.payee",
    },
    {
      header: "payments.fields.value",
    },
    {
      header: "payments.fields.currency",
    },
    {
      header: "payments.fields.state",
    },
    {
      header: "payments.fields.paymentMethod",
    },
    {
      header: "payments.fields.balance",
    },
    {
      header: "payments.fields.paymentDate",
    },
    {
      header: "payments.fields.invoice",
    },
    {
      header: "payments.fields.invoiceUrl",
    },
    {
      header: "payments.fields.notes",
    },
  ],
};

function RouteComponent() {
  const { id } = Route.useParams();
  return (
    <div className="grid gap-4">
      <DepositsTable id={id} />
      <BalancesTable id={id} />
      <Outlet />
    </div>
  );
}

function DepositsTable({ id }: { id: string }) {
  const query = useGetDeposits(id);
  const { data: reservation, isLoading } = useGetReservation(id);
  return (
    <DomainList
      domain="payments"
      titleKey="depositsTitle"
      breadcrumbs={[
        {
          content: (
            <>
              <T tKey="reservations.title" />{" "}
              <BookOpen width={16} height={16} />
            </>
          ),
          href: "/reservations",
        },
        {
          content:
            isLoading || !reservation ? (
              <Skeleton className="h-5 w-10" />
            ) : (
              <>
                {reservation.agencyReservationNumber ?? reservation.id}
                <BookOpen width={16} height={16} />
              </>
            ),
          href: "/reservations/$id",
          params: { id },
        },
        {
          content: (
            <>
              <T tKey="payments.title" /> <PiggyBank width={16} height={16} />
            </>
          ),
          href: "/payments/$id",
          params: { id },
        },
      ]}
      tableOptions={tableDefinition}
      type="table"
      data={query.data}
      isLoading={query.isLoading}
      newLink="/payments/$id/deposit/new"
      newLinkParams={{ id }}
      noOutlet
      renderItem={(payment, index, isTable) => (
        <PaymentCard
          payment={payment}
          key={index}
          reservationId={id}
          isTable={isTable}
        />
      )}
    />
  );
}
function BalancesTable({ id }: { id: string }) {
  const query = useGetBalances(id);
  return (
    <DomainList
      domain="payments"
      titleKey="balancesTitle"
      hidePageHeader
      tableOptions={tableDefinition}
      type="table"
      data={query.data}
      isLoading={query.isLoading}
      newLink="/payments/$id/balance/new"
      newLinkParams={{ id }}
      noOutlet
      renderItem={(payment, index, isTable) => (
        <PaymentCard
          payment={payment}
          key={index}
          reservationId={id}
          isTable={isTable}
        />
      )}
    />
  );
}
